import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { CopyToClipboardButton } from '#src/atoms/buttons/CopyToClipboardButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconHelpCircle } from '#src/atoms/icons/system/iconHelpCircle.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyS, LabelS } from '#src/atoms/typography/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { ConfirmDialog } from '#src/components/form/ConfirmDialog/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useToggle } from '#src/hooks/useToggle.ts';

import type {
  DeleteOAuthApplicationMutation,
  DeleteOAuthApplicationMutationVariables,
  OAuthApplicationRow_oAuthApplication,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  clientId: {
    defaultMessage: 'Client ID',
  },
  clientSecret: {
    defaultMessage: 'Client secret',
  },
  redirectUris: {
    defaultMessage: 'Callback URLs',
  },
  deleteConfirm: {
    defaultMessage:
      'This will permanently delete <b>{name}</b> and revoke its credentials. This cannot be undone.',
  },
});

const fragment = gql`
  fragment OAuthApplicationRow_oAuthApplication on OAuthApplication {
    id
    name
    pictureUrl
    clientId
    clientSecret
    redirectUris
    grantTypes
  }
` as TypedDocumentNode<OAuthApplicationRow_oAuthApplication>;

const DELETE_OAUTH_APPLICATION_MUTATION = gql`
  mutation DeleteOAuthApplicationMutation(
    $input: deleteOAuthApplicationInput!
  ) {
    deleteOAuthApplication(input: $input) {
      currentUser {
        id
        slug
        ownedOAuthApplications {
          id
          ...OAuthApplicationRow_oAuthApplication
        }
      }
      errors {
        message
      }
    }
  }
  ${fragment}
` as TypedDocumentNode<
  DeleteOAuthApplicationMutation,
  DeleteOAuthApplicationMutationVariables
>;

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  padding: var(--double-unit) 0;
  &:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-100);
  }
`;

const Img = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 100%;
  object-fit: cover;
`;

const Credential = styled(Horizontal).attrs({ gap: 1 })`
  code {
    font-family: monospace;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 28ch;
  }
`;

type Props = {
  application: OAuthApplicationRow_oAuthApplication;
};

export const OAuthApplicationRow = ({ application }: Props) => {
  const [showConfirm, toggleConfirm] = useToggle(false);
  const [deleteApplication, { loading }] = useMutation(
    DELETE_OAUTH_APPLICATION_MUTATION
  );

  return (
    <Root>
      <SBHorizontal gap={2}>
        <Horizontal gap={1.5}>
          {application.pictureUrl ? (
            <Img src={application.pictureUrl} alt={application.name} />
          ) : (
            <FontAwesomeIcon icon={iconHelpCircle} size="2x" />
          )}
          <BodyS bold>{application.name}</BodyS>
        </Horizontal>
        <Button
          size="small"
          color="red"
          loading={loading}
          onClick={() => toggleConfirm()}
        >
          <FormattedMessage defaultMessage="Delete" />
        </Button>
      </SBHorizontal>

      <Vertical gap={1}>
        <Credential>
          <LabelS color="var(--c-nd-600)">
            <FormattedMessage {...messages.clientId} />
          </LabelS>
          <code>{application.clientId}</code>
          <CopyToClipboardButton textToCopy={application.clientId} />
        </Credential>
        {!!application.clientSecret && (
          <Credential>
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage {...messages.clientSecret} />
            </LabelS>
            <code>{application.clientSecret}</code>
            <CopyToClipboardButton textToCopy={application.clientSecret} />
          </Credential>
        )}
      </Vertical>

      <Vertical gap={0.5}>
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage {...messages.redirectUris} />
        </LabelS>
        {application.redirectUris.map(uri => (
          <BodyS key={uri}>{uri}</BodyS>
        ))}
      </Vertical>

      <Horizontal gap={1}>
        {application.grantTypes.map(grantType => (
          <Chip key={grantType} color="grey" size="small">
            <LabelS>{grantType}</LabelS>
          </Chip>
        ))}
      </Horizontal>

      <ConfirmDialog
        open={showConfirm}
        onClose={() => toggleConfirm()}
        message={
          <BodyS className="text-center">
            <FormattedMessage
              {...messages.deleteConfirm}
              values={{
                name: application.name,
                b: Bold,
              }}
            />
          </BodyS>
        }
        onConfirm={async () => {
          await deleteApplication({
            variables: { input: { id: application.id } },
          });
        }}
        ctaProps={{ color: 'red' }}
      />
    </Root>
  );
};

OAuthApplicationRow.fragments = {
  oAuthApplication: fragment,
};
